import React from "react";
import { formatCents } from "../../lib/investApi";

const CATEGORY_META = {
  SUV: {
    icon: "🚙",
    label: "SUV",
    subtitle: "Sport Utility Vehicle",
    gradient: "linear-gradient(135deg, #1e3a5f 0%, #2563EB 100%)",
  },
  SEDAN: {
    icon: "🚗",
    label: "Sedan",
    subtitle: "Executive Sedan",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #E11D48 100%)",
  },
  HATCHBACK: {
    icon: "🚕",
    label: "Hatchback",
    subtitle: "Compact Hatchback",
    gradient: "linear-gradient(135deg, #064e3b 0%, #059669 100%)",
  },
  TRUCK: {
    icon: "🛻",
    label: "Truck",
    subtitle: "Pickup Truck",
    gradient: "linear-gradient(135deg, #431407 0%, #ea580c 100%)",
  },
  SPORTS: {
    icon: "🏎️",
    label: "Sports",
    subtitle: "Sports Car",
    gradient: "linear-gradient(135deg, #3b0764 0%, #9333ea 100%)",
  },
};

export default function InvestCategoryCard({ category, collections, onBuy }) {
  const meta = CATEGORY_META[category] ?? { icon: "🚘", label: category, subtitle: category, gradient: "var(--zc-bg-3)" };

  const minted = collections.filter((c) => c.status === "MINTED");
  const totalSupply = minted.reduce((s, c) => s + (c.totalSupply || 0), 0);
  const totalAvailable = minted.reduce((s, c) => s + (c.availableForSale || 0), 0);
  const soldOut = totalAvailable === 0;
  const soldPct = totalSupply > 0 ? Math.round(((totalSupply - totalAvailable) / totalSupply) * 100) : 0;
  const minPrice = minted.length > 0 ? Math.min(...minted.map((c) => c.unitPriceCents)) : 0;

  // pick the cheapest available collection to pass to the buy modal
  const buyTarget = minted
    .filter((c) => c.availableForSale > 0)
    .sort((a, b) => a.unitPriceCents - b.unitPriceCents)[0] ?? null;

  // Use the first minted collection's imageUrl if available
  const heroImageUrl = minted[0]?.imageUrl ?? null;

  return (
    <div className="zc-card" style={{ overflow: "hidden", padding: 0, display: "flex", flexDirection: "column" }}>
      {/* Hero */}
      <div style={{ position: "relative", height: 200, background: meta.gradient, overflow: "hidden" }}>
        {heroImageUrl && (
          <img
            src={heroImageUrl}
            alt={meta.label}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
          />
        )}
        {/* Dark gradient overlay for text legibility */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.08) 100%)" }} />
        {/* Label */}
        <div style={{ position: "absolute", bottom: 14, left: 16 }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 20, letterSpacing: "0.02em", lineHeight: 1.2 }}>{meta.label}</div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, letterSpacing: "0.07em", textTransform: "uppercase", marginTop: 2 }}>{meta.subtitle}</div>
        </div>
        {/* Available badge */}
        <span style={{
          position: "absolute", top: 10, right: 10,
          background: soldOut ? "rgba(0,0,0,0.55)" : "rgba(16,185,129,0.9)",
          color: "#fff", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600,
          backdropFilter: "blur(4px)",
        }}>
          {soldOut ? "Sold Out" : "Available"}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "16px 18px 18px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Price */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: "var(--zc-secondary)" }}>
            {minPrice > 0 ? formatCents(minPrice) : "—"}
          </span>
          <span style={{ color: "var(--zc-text-3)", fontSize: 12 }}>/token</span>
          {minted.length > 1 && (
            <span style={{ color: "var(--zc-text-3)", fontSize: 11, marginLeft: 4 }}>from</span>
          )}
        </div>

        {/* Supply bar */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--zc-text-3)", marginBottom: 5 }}>
            <span>{totalAvailable.toLocaleString()} of {totalSupply.toLocaleString()} available</span>
            <span>{soldPct}% sold</span>
          </div>
          <div style={{ height: 5, background: "var(--zc-bg-3)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${soldPct}%`,
              background: "var(--zc-secondary)",
              borderRadius: 3, transition: "width 0.4s",
            }} />
          </div>
        </div>

        {/* Buy button */}
        <button
          onClick={() => !soldOut && buyTarget && onBuy(buyTarget, meta.label)}
          disabled={soldOut || !buyTarget}
          className={soldOut ? "zc-btn-ghost" : "zc-btn-primary"}
          style={{ marginTop: "auto", width: "100%", opacity: soldOut ? 0.5 : 1 }}
        >
          {soldOut ? "Sold Out" : `Buy ${meta.label} Tokens`}
        </button>
      </div>
    </div>
  );
}
